import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/hooks/use-auth";
import { LanguageProvider } from "@/hooks/use-language";
import { ParentGateProvider } from "@/hooks/use-parent-gate";
import { CookieConsent } from "@/components/CookieConsent";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { AmbientProvider } from "@/hooks/use-ambient";
import { CacheGuard } from "@/components/CacheGuard";

import appCss from "../styles.css?url";

const EARLY_CHUNK_GUARD = `
(() => {
  const flag = "jozori_chunk_recovery_attempted";
  const marker = "jozori_chunk_recovery_last_cleanup";
  const isChunkError = (value) => {
    const message = typeof value === "string" ? value : value && value.message ? String(value.message) : "";
    return message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed") ||
      message.includes("error loading dynamically imported module") ||
      /ChunkLoadError/i.test(message);
  };
  const cleanup = async () => {
    try {
      if (sessionStorage.getItem(flag)) return;
      sessionStorage.setItem(flag, "1");
      localStorage.setItem(marker, String(Date.now()));
    } catch (_) { return; }
    try {
      if (window.caches) await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
    } catch (_) {}
    try {
      if (navigator.serviceWorker) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((reg) => reg.unregister()));
    } catch (_) {}
  };
  window.addEventListener("error", (event) => { if (isChunkError(event.error || event.message)) cleanup(); }, true);
  window.addEventListener("unhandledrejection", (event) => { if (isChunkError(event.reason)) cleanup(); }, true);
})();
`;


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "Jozori — Family Learning" },
      { name: "description", content: "A warm, bilingual learning world for children — stories, videos, and daily growth." },
      { name: "theme-color", content: "#e85d3a" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Jozori" },
      { property: "og:title", content: "Jozori — Family Learning" },
      { property: "og:description", content: "A warm, bilingual learning world for children — stories, videos, and daily growth." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Jozori" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Jozori — Family Learning" },
      { name: "twitter:description", content: "A warm, bilingual learning world for children — stories, videos, and daily growth." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "icon", href: "/icon-192.png", type: "image/png", sizes: "192x192" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;600;700;800;900&family=Tajawal:wght@400;500;700;800&family=Cairo:wght@400;700;900&family=Bricolage+Grotesque:wght@500;700;800&family=Inter:wght@400;500;600;700&display=swap",
      },
      {
        rel: "preload",
        href: "/og-image.jpg",
        as: "image",
        type: "image/jpeg",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <script dangerouslySetInnerHTML={{ __html: EARLY_CHUNK_GUARD }} />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    import("@/lib/chunk-recovery").then(({ installChunkRecovery }) => installChunkRecovery()).catch(() => {});
    import("@/lib/monitoring").then(({ initMonitoring }) => initMonitoring()).catch(() => {});
    import("@/lib/native").then(({ initNative }) => initNative()).catch(() => {});
    import("@/lib/analytics").then(({ initAnalytics }) => initAnalytics()).catch(() => {});
    import("@/lib/perf").then(({ registerServiceWorker }) => registerServiceWorker()).catch(() => {});
  }, []);

  return (
    <RouteErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <CacheGuard />
        <AuthProvider>
          <LanguageProvider>
            <ParentGateProvider>
              <AmbientProvider>
                <Outlet />
                <CookieConsent />
                <Toaster richColors position="top-center" />
              </AmbientProvider>
            </ParentGateProvider>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </RouteErrorBoundary>
  );
}

